import React, { useState } from "react";
import { toResult, type Result, type CheckStatus } from "@sequesign/verifier-browser";

import { bundleToLoadedReceipt } from "../load/bundle.js";
import { runDemoVerification } from "../verify/run-demo.js";
import { runSerialized } from "../verify/serialize.js";

// A distinct Overview LAYOUT for the autonomous-flight demo: a flight record /
// ops-console strip whose legs (dispatch -> sector legs + interleaved
// sub-captures -> recovery) are the actions of ONE sealed mission. Verification
// is honest to the protocol: the whole receipt is verified as a unit, so one
// "Verify the flight record" click checks every leg together and lights them up.
// A toggle switches between two recorded receipts — a conformant flight and one
// that edges over the altitude ceiling. The breach leg is flagged the moment the
// off-mandate flight is selected (the record already shows the over-ceiling
// reading), so WHERE it happened is visible before you verify. All copy + the
// scenario->bundle wiring come from props.

export interface FlightLeg {
  // Which built-in glyph to draw (see ICONS).
  icon: "dispatch" | "sector" | "capture" | "recovery";
  phase: string;
  title: string;
  tele: string;
  // When present, this leg is the mandate breach: shown in red on the telemetry
  // line with a ⚠, flagged amber before verify, and marked non-conformant after.
  flag?: string;
}

export interface FlightScenario {
  key: string;
  toggleLabel: string;
  // Key into the `bundles` map: the recorded flight record this shows.
  bundleId: string;
  legs: FlightLeg[];
  // The bottom summary, shown before verification as the promise and refined by
  // the verdict after.
  summaryTitle: string;
  summaryDetail: string;
  // false when this flight breaches the mandate (conformant:false).
  expectConformant: boolean;
}

export interface FlightRecordConfig {
  eyebrow: string;
  headline: string;
  sub: string;
  recordTitle: string;
  recordSub: string;
  mandate: {
    sectorsLabel: string;
    sectors: string;
    ceilingLabel: string;
    ceiling: string;
  };
  scenarios: FlightScenario[];
  verifyLabel: string;
  verifyingLabel: string;
  reverifyLabel: string;
  browserNote: string;
}

export interface FlightCta {
  label: string;
  sub: string;
  onClick: () => void;
}

const STATUS_MARK: Record<CheckStatus, string> = {
  pass: "✓",
  fail: "✕",
  partial: "◐",
  not_evaluated: "–"
};

const ICONS: Record<FlightLeg["icon"], React.ReactNode> = {
  dispatch: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20V8M12 8l-4 4M12 8l4 4M5 20h14" />
    </svg>
  ),
  sector: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 21s7-5.5 7-11a7 7 0 0 0-14 0c0 5.5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  ),
  capture: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="13" r="4" />
      <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" />
    </svg>
  ),
  recovery: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4v12M12 16l-4-4M12 16l4 4M5 20h14" />
    </svg>
  )
};

export function FlightRecord({
  config,
  bundles,
  ctas
}: {
  config: FlightRecordConfig;
  bundles: Record<string, Record<string, string>>;
  ctas: FlightCta[];
}) {
  const [active, setActive] = useState<string>(config.scenarios[0]?.key ?? "");
  const [results, setResults] = useState<Record<string, Result>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<boolean>(false);

  const scenario = config.scenarios.find((s) => s.key === active) ?? config.scenarios[0];
  const result = results[scenario.bundleId] ?? null;
  const error = errors[scenario.bundleId] || null;
  const verified = result !== null;

  async function verify(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setErrors((e) => ({ ...e, [scenario.bundleId]: "" }));
    try {
      const bundle = bundles[scenario.bundleId];
      if (!bundle) throw new Error(`No recorded bundle for ${scenario.bundleId}`);
      const loaded = bundleToLoadedReceipt(scenario.bundleId, bundle);
      const started = performance.now();
      // Serialized process-wide (shared virtual FS): never overlap another run.
      const { report } = await runSerialized(() => runDemoVerification(loaded));
      const totalMs = performance.now() - started;
      const r = toResult(report, { totalMs, verifierVersion: "demo", commit: "demo" });
      setResults((prev) => ({ ...prev, [scenario.bundleId]: r }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [scenario.bundleId]: err instanceof Error ? err.message : String(err)
      }));
    } finally {
      setBusy(false);
    }
  }

  const verifiedValid = verified && result?.valid;

  return (
    <section className="flt">
      <div className="flt-head-copy">
        <div className="flt-eyebrow">{config.eyebrow}</div>
        <h1 className="flt-headline">{config.headline}</h1>
        <p className="flt-sub">{config.sub}</p>
      </div>

      <div className="flt-record">
        <div className="flt-meta">
          <div>
            <div className="flt-title">{config.recordTitle}</div>
            <div className="flt-metasub">{config.recordSub}</div>
          </div>
          <div className="flt-mandate">
            <div className="mfield">
              <div className="mfield-k">{config.mandate.sectorsLabel}</div>
              <div className="mfield-v">{config.mandate.sectors}</div>
            </div>
            <div className="mfield">
              <div className="mfield-k">{config.mandate.ceilingLabel}</div>
              <div className="mfield-v">{config.mandate.ceiling}</div>
            </div>
          </div>
        </div>

        <div className="flt-toggle" role="tablist" aria-label="Flight scenario">
          {config.scenarios.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={s.key === active}
              className={"flt-toggle-btn" + (s.key === active ? " flt-toggle-btn-active" : "")}
              onClick={() => setActive(s.key)}
              disabled={busy}
            >
              {s.toggleLabel}
            </button>
          ))}
        </div>

        <div className="strip">
          {scenario.legs.map((leg, i) => {
            const isBreach = Boolean(leg.flag);
            // Pre-verify: flag the breach leg (its telemetry already shows the
            // over-ceiling reading). Post-verify: breach -> bad, others -> done.
            const stateClass = verifiedValid
              ? isBreach
                ? " leg-bad"
                : " leg-done"
              : isBreach
                ? " leg-flagged"
                : "";
            return (
              <div key={i} className={"leg" + stateClass}>
                <span className="leg-dot" aria-hidden="true">
                  {ICONS[leg.icon]}
                </span>
                <div className="leg-body">
                  <div className="leg-phase">{leg.phase}</div>
                  <div className="leg-title">
                    {leg.title}
                    {isBreach && (
                      <span className="leg-warn" aria-hidden="true">
                        {" "}
                        ⚠
                      </span>
                    )}
                  </div>
                  <div className="leg-tele">
                    {leg.tele}
                    {leg.flag && <span className="leg-flag"> · {leg.flag}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flt-verify">
          <button className="flt-verify-btn" onClick={verify} disabled={busy}>
            {busy ? config.verifyingLabel : verified ? config.reverifyLabel : config.verifyLabel}
          </button>
        </div>

        {error && <div className="flt-error">Verification error: {error}</div>}

        <FlightSummary scenario={scenario} result={result} />
      </div>

      <p className="flt-browser-note">{config.browserNote}</p>

      <div className="flt-ctas">
        {ctas.map((cta, i) => (
          <button className="flt-cta" key={i} onClick={cta.onClick}>
            <span className="flt-cta-label">{cta.label}</span>
            <span className="flt-cta-sub">{cta.sub}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function FlightSummary({ scenario, result }: { scenario: FlightScenario; result: Result | null }) {
  if (!result) {
    return (
      <div className="flt-summary flt-summary-idle">
        <div className="flt-summary-head">
          <span className="flt-summary-mark" aria-hidden="true">
            •
          </span>
          <span className="flt-summary-title">{scenario.summaryTitle}</span>
        </div>
        <p className="flt-summary-detail">{scenario.summaryDetail}</p>
      </div>
    );
  }

  const valid = result.valid;
  const conformant = result.conformant;
  const cls = !valid
    ? "flt-summary-invalid"
    : conformant === false
      ? "flt-summary-warn"
      : "flt-summary-valid";
  const title = !valid
    ? "Verification failed"
    : conformant === false
      ? "Flight record verified: authentic, but NOT conformant"
      : "Flight record verified: conformant";
  const detail = !valid
    ? `Rejected: ${result.reason ?? "see the failed check below."}`
    : conformant === false
      ? "Every leg is still sealed, witnessed, and authentic, but the record honestly shows the drone exceeded the mandate's altitude ceiling (conformant: false). The mandate caught the breach to the foot; the record didn't round it down."
      : "Every leg is sealed, witnessed, and bound to the flight mandate, and the drone stayed inside the authorized sectors and under the altitude ceiling the whole flight. Anyone can verify the record offline.";

  return (
    <div className={"flt-summary " + cls}>
      <div className="flt-summary-head">
        <span className="flt-summary-mark" aria-hidden="true">
          {valid ? "✓" : "✕"}
        </span>
        <span className="flt-summary-title">{title}</span>
      </div>
      <p className="flt-summary-detail">{detail}</p>

      {result.trustAnchorMode === "self" && (
        <p className="flt-summary-anchor">
          Integrity self-check: verified against the witness key carried in this recorded receipt,
          not an independent third-party anchor. The <strong>Run live</strong> tab anchors to the
          witness's published key for a genuine third-party check.
        </p>
      )}

      <details className="flt-checks">
        <summary>Show the {result.checks.length} checks the verifier ran</summary>
        <ul className="flt-check-list">
          {result.checks.map((c) => (
            <li key={c.id} className={`flt-check flt-check-${c.status}`}>
              <span className="flt-check-mark" aria-hidden="true">
                {STATUS_MARK[c.status]}
              </span>
              <span className="flt-check-detail">{c.detail}</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
